/*
  The 6Hats sign-in chooser.

  A small dialog with the accessibility mechanics done properly rather than
  approximately: focus moves in on open and back to the trigger on close, Tab
  is trapped inside the panel, Escape and a click outside both dismiss it, and
  the rest of the page is hidden from assistive technology while it is open.

  It is progressive. The account icon is a real link to Shopify's sign-in page
  in the markup, so with this script blocked — or before it loads — clicking it
  simply goes to the normal 6Hats login. The chooser only ever *adds* the
  student option; it never removes the ordinary route.
*/
(function () {
  "use strict";

  var FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  var dialog = document.querySelector("[data-samashti-chooser]");
  if (!dialog) return;

  /*
    The dialog is authored inside the header section, because its copy is
    merchant-editable from the Header block in the theme editor. But the sticky
    header translates itself on scroll, and a `position: fixed` descendant of a
    transformed element is positioned against that element rather than the
    viewport — so the panel would slide away with the header.

    Reparenting to <body> once on load fixes the containing block while keeping
    the settings where the merchant expects to find them.
  */
  if (dialog.parentElement !== document.body) {
    document.body.appendChild(dialog);
  }

  var trigger = null;
  var isOpen = false;

  function visibleFocusable() {
    return Array.prototype.filter.call(
      dialog.querySelectorAll(FOCUSABLE),
      function (element) {
        if (element.hasAttribute("hidden")) return false;
        if (element.getAttribute("aria-hidden") === "true") return false;
        if (element.getAttribute("tabindex") === "-1") return false;
        // Skips the groups CSS has hidden for the current session state, so Tab
        // never lands on an offer that is not on screen.
        return element.offsetParent !== null;
      }
    );
  }

  /**
   * Hides everything outside the dialog from assistive technology. Without
   * this, a screen reader can still walk the page behind a visually modal
   * panel — the focus trap only constrains the Tab key.
   */
  var inerted = [];

  function applyInert() {
    inerted = [];
    Array.prototype.forEach.call(document.body.children, function (child) {
      if (child === dialog || child.contains(dialog)) return;
      if (child.getAttribute("aria-hidden") === "true") return;
      child.setAttribute("aria-hidden", "true");
      inerted.push(child);
    });
  }

  function releaseInert() {
    inerted.forEach(function (child) {
      child.removeAttribute("aria-hidden");
    });
    inerted = [];
  }

  function open(fromElement) {
    if (isOpen) return;
    isOpen = true;
    trigger = fromElement || null;

    dialog.hidden = false;
    applyInert();
    // Locking the document rather than the body alone keeps iOS from scrolling
    // the page behind the sheet.
    document.documentElement.style.overflow = "hidden";

    var focusable = visibleFocusable();
    (focusable[0] || dialog).focus();
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    dialog.hidden = true;
    releaseInert();
    document.documentElement.style.overflow = "";

    if (trigger && typeof trigger.focus === "function") trigger.focus();
    trigger = null;
  }

  function onKeydown(event) {
    if (!isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== "Tab") return;

    var focusable = visibleFocusable();
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (focusable.indexOf(active) === -1) {
      // Focus escaped the panel — pull it back rather than letting the visitor
      // tab silently into the hidden page behind.
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener("keydown", onKeydown);

  dialog.addEventListener("click", function (event) {
    if (
      event.target &&
      event.target.closest &&
      event.target.closest("[data-samashti-chooser-close]")
    ) {
      event.preventDefault();
      close();
    }
  });

  /*
    The account icon.

    Only intercepted when the visitor is *not* already a signed-in Shopify
    customer — Liquid marks the trigger for that case, and an existing customer
    keeps the untouched Account behaviour. A modified click (new tab, download,
    middle button) is always left alone.
  */
  document.addEventListener("click", function (event) {
    var openTrigger = event.target && event.target.closest
      ? event.target.closest("[data-samashti-chooser-open]")
      : null;
    if (!openTrigger) return;
    if (event.defaultPrevented) return;
    // "student-only": the merchant has hidden the Samashti option, so only a
    // signed-in student (who needs Sign out) gets the chooser. Everyone else
    // follows the link to the normal 6Hats login.
    if (
      openTrigger.getAttribute("data-samashti-chooser-open") === "student-only" &&
      !document.documentElement.classList.contains("samashti-session-active")
    ) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button && event.button !== 0) return;

    event.preventDefault();
    open(openTrigger);
  });
})();
